import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Gem, Droplet, ShoppingBag, Sparkles, ArrowRight } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { usePromo } from "../context/PromoContext";
import { fileUrl } from "../lib/api";

// ── Reusable, CMS-driven Website Banner Module ───────────────────────────────
// A single component driven by placement-keyed settings (settings.banners[placement]).
// Supports two modes: an editable "How to Order" guide and a scheduled custom image
// banner. Drop <WebsiteBanner placement="homepage" /> anywhere later — no new code.

export const DEFAULT_HOMEPAGE_BANNER = {
  enabled: true,
  autoplay: true,
  autoplay_speed: 5000,

  // Temporary compatibility for the current editor.
  mode: "custom_image",

  how_to_order: {
    heading: "",
    subheading: "",
    steps: [],
    footer: "",
  },

  custom_image: {
    enabled: true,
    desktop_image: "",
    mobile_image: "",
    heading: "",
    subheading: "",
    cta_text: "",
    cta_link: "",
    cta_enabled: true,
    open_new_tab: false,
    start_date: "",
    end_date: "",
  },

  // New structure we'll migrate to.
  slides: [
    {
      id: "slide-1",
      enabled: true,
      order: 1,
      desktop_image: "",
      mobile_image: "",
      title: "Welcome to HYPPE AROMA",
      subtitle: "Enhance Your Natural Aura",
      cta_text: "Shop Now",
      cta_link: "/shop",
      open_new_tab: false,
      start_date: "",
      end_date: "",
    },
  ],
};

export const DEFAULT_LIBRARY_BANNER = {
  enabled: true,
  mode: "how_to_order",
  how_to_order: {
    heading: "Create Your Signature Fragrance in 4 Easy Steps",
    subheading: "From search to doorstep delivery in just a few clicks.",
    steps: [
      { title: "Search", description: "Search by fragrance, mood or occasion." },
      { title: "Customize", description: "Choose your preferred concentration — EDP, Parfum or Attar." },
      { title: "Personalize", description: "Select your bottle size, bottle style and packaging. Mix two fragrances or keep a single note." },
      { title: "Checkout", description: "Review your selection and place your order. Every fragrance is hand-blended after the order is received." },
    ],
    footer: "Over 3,000+ Possible Fragrance Combinations",
  },
  custom_image: {
    enabled: true, desktop_image: "", mobile_image: "", heading: "", subheading: "",
    cta_text: "", cta_link: "", cta_enabled: true, open_new_tab: false, start_date: "", end_date: "",
  },
};

const STEP_ICONS = [Search, Gem, Droplet, ShoppingBag];

const withinSchedule = (start, end) => {
  const today = new Date().toISOString().slice(0, 10);
  if (start && today < start) return false;
  if (end && today > end) return false;
  return true;
};

// Resolve raw settings → what should actually render (or null to hide the section).
export const resolveBanner = (raw, placement = "library") => {
  const defaults =
    placement === "homepage"
      ? DEFAULT_HOMEPAGE_BANNER
      : DEFAULT_LIBRARY_BANNER;

  const b = { ...defaults, ...(raw || {}) };

  console.log("DEBUG HOMEPAGE BANNER", {
    placement,
    raw,
    enabled: b.enabled,
    slides: b.slides,
  });

  if (placement === "homepage") {
    if (b.enabled === false) return null;

    const slides = (b.slides || [])
      .filter((s) => s.enabled !== false)
      .filter((s) => withinSchedule(s.start_date, s.end_date))
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((slide) => ({
        media_type: slide.media_type || "image",

        desktop_image: slide.desktop_image || slide.image,
        mobile_image:
          slide.mobile_image ||
          slide.mobileImage ||
          slide.desktop_image ||
          slide.image,

        desktop_video: slide.desktop_video || "",
        mobile_video: slide.mobile_video || slide.desktop_video || "",

        desktop_poster: slide.desktop_poster || "",
        mobile_poster:
          slide.mobile_poster || slide.desktop_poster || "",

        heading: slide.title,
        subheading: slide.subtitle,

        cta_text: slide.cta_text || slide.ctaText,
        cta_link: slide.cta_link || slide.ctaLink,

        cta_enabled: slide.enabled !== false,
        open_new_tab: slide.open_new_tab ?? slide.newTab ?? false,
      }));

    if (!slides.length) return null;

    return {
      kind: "carousel",
      autoplay: b.autoplay !== false,
      autoplay_speed: Number(b.autoplay_speed || 5000),
      slides,
    };
  }

  if (b.enabled === false) return null;

  const guide = {
    kind: "how_to_order",
    data: {
      ...DEFAULT_LIBRARY_BANNER.how_to_order,
      ...(b.how_to_order || {}),
    },
  };

  if (b.mode === "custom_image") {
    const c = {
      ...DEFAULT_LIBRARY_BANNER.custom_image,
      ...(b.custom_image || {}),
    };

    const active =
      c.enabled !== false &&
      withinSchedule(c.start_date, c.end_date) &&
      (c.desktop_image || c.mobile_image);

    return active ? { kind: "custom", data: c } : guide;
  }

  return guide;
};

function HowToOrder({ data }) {
  const steps = (data.steps || []).slice(0, 4);
  return (
    <div className="relative border border-[#C5A059]/30 bg-[#120309]/50 backdrop-blur-sm p-7 sm:p-8">
      <span className="pointer-events-none absolute top-0 right-0 w-16 h-16 border-t border-r border-[#C5A059]/40" />
      <span className="pointer-events-none absolute bottom-0 left-0 w-16 h-16 border-b border-l border-[#C5A059]/40" />
      <p className="font-mono-label text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-3">◐ How to Order</p>
      <h3 className="font-serif-display text-2xl sm:text-[26px] leading-tight text-[#F4EFE6]">{data.heading}</h3>
      {data.subheading && <p className="mt-2 text-sm text-white/55 leading-relaxed">{data.subheading}</p>}

      <div className="mt-6 space-y-5">
        {steps.map((s, i) => {
          const Icon = STEP_ICONS[i] || Sparkles;
          return (
            <div key={i} data-testid={`banner-step-${i}`} className="flex gap-4 group">
              <div className="relative shrink-0">
                <div className="w-11 h-11 border border-[#C5A059]/40 bg-[#C5A059]/[0.06] flex items-center justify-center text-[#C5A059] transition-colors group-hover:bg-[#C5A059]/15">
                  <Icon className="w-[18px] h-[18px]" strokeWidth={1.6} />
                </div>
                <span className="absolute -top-2 -left-2 w-5 h-5 rounded-full bg-[#C5A059] text-[#0A0A0A] font-mono-label text-[9px] flex items-center justify-center">{i + 1}</span>
                {i < steps.length - 1 && <span className="absolute left-1/2 -translate-x-1/2 top-12 h-[calc(100%-1rem)] w-px bg-gradient-to-b from-[#C5A059]/40 to-transparent" />}
              </div>
              <div className="pb-1">
                <p className="font-mono-label text-[11px] uppercase tracking-[0.22em] text-[#F4EFE6]">{s.title}</p>
                <p className="mt-1 text-[13px] text-white/55 leading-relaxed">{s.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {data.footer && (
        <div className="mt-7 flex items-center gap-2.5 border border-[#C5A059]/40 bg-[#C5A059]/10 px-4 py-3">
          <Sparkles className="w-4 h-4 text-[#C5A059] shrink-0" strokeWidth={1.6} />
          <span className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-[#C5A059] leading-snug">{data.footer}</span>
        </div>
      )}
    </div>
  );
}

function CustomBanner({ data }) {
  const desktop = fileUrl(data.desktop_image || data.mobile_image);
  const mobile = fileUrl(data.mobile_image || data.desktop_image);
  const hasCta = data.cta_enabled !== false && data.cta_text && data.cta_link;
  const hasOverlay = data.heading || data.subheading || hasCta;

  const inner = (
    <div className="relative overflow-hidden border border-[#C5A059]/30 group">
      {data.media_type === "video" ? (
        <>
          <video
            src={fileUrl(data.desktop_video)}
            poster={fileUrl(data.desktop_poster)}
            className="hidden sm:block w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />

          <video
            src={fileUrl(data.mobile_video || data.desktop_video)}
            poster={fileUrl(data.mobile_poster || data.desktop_poster)}
            className="block sm:hidden w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
        </>
      ) : (
       <>
         <img
           src={desktop}
           alt={data.heading || "Promotion"}
           className="hidden sm:block w-full h-full object-cover"
         />

         <img
           src={mobile}
           alt={data.heading || "Promotion"}
           className="block sm:hidden w-full h-full object-cover"
         />
       </>
     )}  
      {hasOverlay && (
        <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-7 bg-gradient-to-t from-[#0A0A0A]/85 via-[#0A0A0A]/25 to-transparent">
          {data.heading && <h3 className="font-serif-display text-2xl sm:text-3xl text-[#F4EFE6] leading-tight">{data.heading}</h3>}
          {data.subheading && <p className="mt-2 text-sm text-white/70 leading-relaxed max-w-md">{data.subheading}</p>}
          {hasCta && (
            <span data-testid="banner-cta" className="mt-4 inline-flex items-center gap-2 self-start px-5 py-2.5 bg-[#C5A059] text-[#0A0A0A] font-mono-label text-[11px] uppercase tracking-[0.2em] group-hover:bg-[#d8b877] transition-colors">
              {data.cta_text} <ArrowRight className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      )}
    </div>
  );

  const link = data.cta_link;
  if (!link) return inner;
  const external = /^https?:\/\//i.test(link);
  if (external || data.open_new_tab) {
    return <a data-testid="banner-link" href={link} target="_blank" rel="noreferrer" className="block">{inner}</a>;
  }
  return <Link data-testid="banner-link" to={link} className="block">{inner}</Link>;
}

export function WebsiteBanner({ placement = "library", config, className = "" }) {
  const settings = useSettings();
  const { campaign } = usePromo();

  const raw = config !== undefined ? config : settings.banners?.[placement];
  const resolved = resolveBanner(raw, placement);

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!resolved) return;
    if (resolved.kind !== "carousel") return;
    if (!resolved.autoplay) return;
    if (resolved.slides.length <= 1) return;

    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % resolved.slides.length);
    }, resolved.autoplay_speed || 5000);

    return () => clearInterval(timer);
  }, [
    resolved?.kind,
    resolved?.autoplay,
    resolved?.autoplay_speed,
    resolved?.slides?.length,
  ]);

  if (placement === "homepage" && campaign?.show_banner) {
    return null;
  }

  if (!resolved) return null;

  if (resolved.kind === "carousel") {
    const slide = resolved.slides[index];
    
    console.log("Current Slide", index, slide);

    return (
      <div
        data-testid={`website-banner-${placement}`}
        className={className}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
          >
            <CustomBanner data={slide} />
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  const key =
    resolved.kind === "custom"
      ? `custom-${resolved.data.desktop_image}-${resolved.data.mobile_image}`
      : "guide";

  return (
    <div
      data-testid={`website-banner-${placement}`}
      className={className}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={key}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45 }}
        >
          {resolved.kind === "how_to_order" ? (
            <HowToOrder data={resolved.data} />
          ) : (
            <CustomBanner data={resolved.data} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
export default WebsiteBanner;
